import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { prop } from '@ipfabric/utils/fp/prop';
import type {
  DefinedUseQueryResult,
  UseQueryResult,
} from '@tanstack/react-query';
import { LoaderAndText } from 'containers/LoaderAndText/LoaderAndText';
import { record } from 'fp-ts';
import type { ReactNode } from 'react';

type ReactQueryState = 'success' | 'pending' | 'error';

type RenderQueryConfig<TResult> = {
  error?: () => ReactNode;
  pending?: () => ReactNode;
  query: UseQueryResult<TResult, unknown>;
  // If false, shows loading state on initial mount until fresh data is fetched,
  // even if cached data is available. If true, displays cached data immediately on mount (if available),
  // without waiting for fresh data.
  renderStaleDataOnMount: boolean;
  success: (data: TResult) => ReactNode;
};

const isInitialFetching = (query: UseQueryResult<unknown, unknown>) =>
  !query.isFetchedAfterMount &&
  (query.fetchStatus === 'fetching' || query.isFetching);

/**
 * Shortcut to render content depending on useQuery result
 *
 * Includes default components for pending and error states,
 * customizable via `nonSuccessRenderers` parameter.
 *
 * @param renderStaleDataOnMount If false, shows loading state on initial mount until fresh data is fetched,
 * even if cached data is available. If true, displays cached data immediately on mount (if available),
 * without waiting for fresh data.
 * @example
 * const query = useQuery(...);
 *
 * return <div>
 *   {renderQuery({ query, success: (data) => (
 *     <p>User name: {data.name}</p>
 *   )})}
 * </div>;
 */
export const renderQuery = <TResult = any>({
  error,
  pending,
  query,
  renderStaleDataOnMount,
  success,
}: RenderQueryConfig<TResult>): ReactNode => {
  const defaultRenderers = {
    pending: () => <LoaderAndText />,
    error: () => <LoaderAndText hasError errorText='Failed to load data' />,
  };

  const renderers = {
    ...defaultRenderers,
    ...(pending && { pending }),
    ...(error && { error }),
  };

  if (!renderStaleDataOnMount && isInitialFetching(query)) {
    return renderers.pending();
  }

  if (query.status === 'success') {
    return success(query.data);
  }

  return renderers[query.status]?.();
};

const getAggregatedStatus = (
  // intentionally listed ReactQueryState so this will emit error
  // if the type is extended in the future - in that case this function
  // would return no error but misleading results.
  existingStates: Set<'pending' | 'error' | 'success'>,
): ReactQueryState => {
  if (existingStates.has('error')) {
    // Whenever error exists, all other states do not matter.
    return 'error';
  }
  if (existingStates.has('pending')) {
    // There is no error and something is still loading.
    return 'pending';
  }
  if (existingStates.has('success')) {
    // Everything is loaded, no other state is present.
    return 'success';
  }
  stumble('getAggregatedStatus used with empty loading state list');
  return 'error';
};

type DefinedResultQueriesRecord<TResults> = {
  [Key in keyof TResults]: DefinedUseQueryResult<TResults[Key]>;
};

const getAggregatedDataFromQueries: <TResults>(
  input: DefinedResultQueriesRecord<TResults>,
) => TResults = record.map(prop('data' as never)) as any;

type RenderQueriesConfig<TResults> = {
  error?: () => ReactNode;
  pending?: () => ReactNode;
  queries: { [Key in keyof TResults]: UseQueryResult<TResults[Key], unknown> };
  // If false, shows loading state on initial mount until fresh data is fetched,
  // even if cached data is available. If true, displays cached data immediately on mount (if available),
  // without waiting for fresh data.
  renderStaleDataOnMount: boolean;
  success: (data: TResults) => ReactNode;
};

/**
 * Shortcut to render content depending on multiple useQuery result
 *
 * Results are aggregated, for example if only one failed, error is rendered instead of all content.
 * See implementation of getAggregatedStatus for details.
 *
 * @see renderQuery
 * @see getAggregatedStatus
 * @param renderStaleDataOnMount If false, shows loading state on initial mount until fresh data is fetched,
 * even if cached data is available. If true, displays cached data immediately on mount (if available),
 * without waiting for fresh data.
 * @example
 * const name = useQuery(...);
 * const surname = useQuery(...);
 *
 * return <div>
 *  {renderQueries({
 *    queries: {
 *      name,
 *      surname,
 *    },
 *    success: ({ name, surname }) => (
 *      <p>Full name: {name} {surname}</p>
 *    ),
 *  })}
 * </div>;
 */
export const renderQueries = <TResults = unknown>({
  error,
  pending,
  queries,
  renderStaleDataOnMount,
  success,
}: RenderQueriesConfig<TResults>): ReactNode => {
  const queryList = Object.values<UseQueryResult<unknown, unknown>>(queries);

  const aggregatedStatus = getAggregatedStatus(
    new Set(queryList.map(prop('status'))),
  );
  const defaultRenderers = {
    pending: () => <LoaderAndText />,
    error: () => <LoaderAndText hasError errorText='Failed to load data' />,
  };

  const renderers = {
    ...defaultRenderers,
    ...(pending && { pending }),
    ...(error && { error }),
  };

  if (!renderStaleDataOnMount && queryList.some(isInitialFetching)) {
    // If any of the queries is not yet fetched after mount,
    // we consider the aggregated state as not fetched after mount.
    return renderers.pending();
  }

  if (aggregatedStatus === 'success') {
    const data = getAggregatedDataFromQueries(
      queries as DefinedResultQueriesRecord<TResults>,
    );
    return success(data);
  }

  return renderers[aggregatedStatus]?.();
};
